import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@store/authStore';
import { useAppStore } from '@store/appStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { RiFileTextLine, RiPulseFill, RiInboxLine, RiPlaneLine, RiCheckLine, RiSendPlaneLine, RiMapPinLine, RiDraftLine, RiCloseLine, RiHourglass2Line, RiArrowRightLine } from '@remixicon/react';
import { Card, CardContent } from '@components/ui/Card';
import { MessagesInbox } from '@/features/messages';
import { Skeleton } from '@components/ui/Skeleton';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, SCOPES, ROLES, normalizeRole } from '@/lib/permissions';
/**
 * Dashboard Page
 * Main dashboard with bids inbox, stats, recent activity, and shortlist
 */
const Dashboard = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { userProfile } = useAuthStore();
  const { updateBidOption } = useAppStore();
  const profileId = userProfile?.userprofile_id;
  const orgId = userProfile?.org_id;
  const role = normalizeRole(userProfile?.role);
  const canApproveSubmissions = role === ROLES.ADMIN || role === ROLES.SUPER_ADMIN;

  // Per-role scope from the permission matrix — same rule as /quotes:
  //   self  → broker sees only their own quotes (created_by)
  //   global → admin/finance/coordinator/compliance see the whole org (broker_org_id)
  const { scope: getScope } = usePermissions();
  const bookingScope = getScope(MODULES.BOOKING);
  const scopeFilter = useMemo(() => {
    if (bookingScope === SCOPES.GLOBAL && orgId) return { broker_org_id: orgId };
    if (bookingScope === SCOPES.SELF && profileId) return { created_by: profileId };
    return null;
  }, [bookingScope, orgId, profileId]);
  const scopeReady = !!scopeFilter;

  // Fetch recent quotes (last 2 weeks) for activity feed
  const twoWeeksAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString();
  }, []);

  const { data: recentQuotes = [], isLoading: recentLoading } = useQuery({
    queryKey: ['quotes', 'recent-activity', bookingScope, profileId, orgId],
    queryFn: async () => {
      const data = await apiClient.getQuotes(0, 50, { ...scopeFilter, created_at_from: twoWeeksAgo });
      return data.map(q => ({ ...q, id: q.quote_id }));
    },
    enabled: scopeReady,
    staleTime: 60_000,
  });

  const recentActivities = useMemo(() => {
    const statusMap = {
      DRAFT: { icon: <RiDraftLine className="w-4 h-4 text-gray-600" />, iconBg: 'bg-gray-100', title: 'Quote Created' },
      SENT: { icon: <RiSendPlaneLine className="w-4 h-4 text-blue-600" />, iconBg: 'bg-blue-100', title: 'Quote Sent' },
      ACCEPTED: { icon: <RiCheckLine className="w-4 h-4 text-green-600" />, iconBg: 'bg-green-100', title: 'Quote Booked' },
      REJECTED: { icon: <RiCloseLine className="w-4 h-4 text-red-600" />, iconBg: 'bg-red-100', title: 'Quote Rejected' },
      IN_TRANSIT: { icon: <RiPlaneLine className="w-4 h-4 text-amber-600" />, iconBg: 'bg-amber-100', title: 'In Transit' },
    };

    const timeAgo = (dateStr) => {
      const diff = Date.now() - new Date(dateStr).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 60) return `${mins}m`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h`;
      const days = Math.floor(hrs / 24);
      return `${days}d`;
    };

    return recentQuotes
      .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
      .map((quote) => {
        const status = quote.status || 'DRAFT';
        const config = statusMap[status] || statusMap.DRAFT;
        const clientName = [quote.client?.first_name, quote.client?.last_name].filter(Boolean).join(' ') || 'Unknown Client';
        const legs = quote.bid_items?.[0]?.bid?.leg_itinerary || [];
        const route = legs.length > 0
          ? legs.map(l => `${l.departure_airport} → ${l.arrival_airport}`).join(', ')
          : quote.departure_airport && quote.arrival_airport
            ? `${quote.departure_airport} → ${quote.arrival_airport}`
            : '';
        const operator = quote.bid_items?.[0]?.bid?.bid_options?.[0]?.operator;
        const operatorName = operator?.dba_name || operator?.name || '';
        const option = quote.bid_items?.[0]?.bid?.bid_options?.[0];
        const price = quote.total_due || option?.total_price || option?.price;
        const priceStr = price && Number(price) > 0 ? `$${Number(price).toLocaleString()}` : '';

        const tags = [];
        if (legs.length > 0) {
          legs.forEach(l => tags.push({ icon: <RiMapPinLine className="w-4 h-4" />, label: `${l.departure_airport} → ${l.arrival_airport}` }));
        } else if (quote.departure_airport && quote.arrival_airport) {
          tags.push({ icon: <RiMapPinLine className="w-4 h-4" />, label: `${quote.departure_airport} → ${quote.arrival_airport}` });
        }
        const tailNumber = quote.bid_items?.[0]?.bid?.bid_options?.[0]?.aircraft?.tail_number;
        if (tailNumber) tags.push({ icon: <RiPlaneLine className="w-4 h-4" />, label: tailNumber });

        let description = clientName;
        if (status=="ACCEPTED") {
          description = `${operatorName} quoted ${priceStr} for ${route}`;
        } else if (status=="SENT") {
          description = `${operatorName} sent quote for ${route}`;
        } else if (route) {
          description = `${clientName} – ${route}`;
        }

        return {
          id: quote.id,
          ...config,
          description,
          tags,
          time: timeAgo(quote.updated_at || quote.created_at),
        };
      });
  }, [recentQuotes]);

  // Fetch quotes for dashboard stats
  const { data: quotes = [], isLoading: quotesLoading } = useQuery({
    queryKey: ['quotes', 'dashboard', bookingScope, profileId, orgId],
    queryFn: async () => {
      const data = await apiClient.getQuotes(0, 100, { ...scopeFilter });
      return data.filter(quote =>
        quote.bid_items?.some(bidItem =>
          bidItem.bid?.bid_options?.some(option =>
            option.shortlisted == true || option.shortlisted === 1
          )
        )
      ).map(quote => ({
        ...quote,
        id: quote.quote_id,
        status: quote.status || 'DRAFT'
      }));
    },
    enabled: scopeReady,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: 2,
    retryDelay: 1000,
  });


  // -------- Pending Approvals (admin / super_admin only) --------
  // Two parallel queries — one per status. Backend accepts a single status
  // filter today, so we fan out and merge client-side.
  const { data: pendingQuoteApprovals = [], isLoading: pendingQuoteLoading } = useQuery({
    queryKey: ['quotes', 'pending-approval', orgId],
    queryFn: async () =>
      apiClient.getQuotes(0, 50, { broker_org_id: orgId, status: 'PENDING_APPROVAL' }),
    enabled: canApproveSubmissions && !!orgId,
    staleTime: 60_000,
  });
  const { data: pendingTripsheetApprovals = [], isLoading: pendingTripsheetLoading } = useQuery({
    queryKey: ['quotes', 'tripsheet-pending-approval', orgId],
    queryFn: async () =>
      apiClient.getQuotes(0, 50, { broker_org_id: orgId, status: 'TRIPSHEET_PENDING_APPROVAL' }),
    enabled: canApproveSubmissions && !!orgId,
    staleTime: 60_000,
  });

  const pendingApprovals = useMemo(() => {
    const rows = [
      ...pendingQuoteApprovals.map(q => ({
        kind: 'QUOTE',
        quoteId: q.quote_id,
        quoteNumber: q.quote_number,
        client: q.client,
        legs: q.bid_items?.[0]?.bid?.leg_itinerary || [],
        submittedAt: q.updated_at || q.created_at,
      })),
      ...pendingTripsheetApprovals.map(q => ({
        kind: 'TSHEET',
        quoteId: q.quote_id,
        quoteNumber: q.quote_number,
        client: q.client,
        legs: q.bid_items?.[0]?.bid?.leg_itinerary || [],
        submittedAt: q.updated_at || q.created_at,
      })),
    ];
    return rows.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
  }, [pendingQuoteApprovals, pendingTripsheetApprovals]);
  const pendingLoading = pendingQuoteLoading || pendingTripsheetLoading;

  const openPendingApproval = useCallback((row) => {
    if (row.kind === 'TSHEET') {
      navigate(`/trip-sheet?quoteid=${row.quoteId}`);
    } else {
      // Admin edit flow — same route used from QuoteDetailsDrawer's Edit button.
      navigate(`/quotes/generate?quoteid=${row.quoteId}`);
    }
  }, [navigate]);

  /**
   * React Query mutation for shortlisting bid options
   * Automatically invalidates and refetches the bidOptions cache
   */
  const shortlistMutation = useMutation({
    mutationFn: async (bidOptionData) => {
      const newShortlistedStatus = !bidOptionData.shortlisted;
      return await apiClient.updateBidOption(bidOptionData.bid_option_id, {
        ...bidOptionData,
        shortlisted: newShortlistedStatus
      });
    },
    onSuccess: (data, bidOptionData) => {
      const newShortlistedStatus = !bidOptionData.shortlisted;
      // Optimistically update Zustand store
      updateBidOption(bidOptionData.bid_option_id, { shortlisted: newShortlistedStatus });
      // Invalidate and refetch both bid options and shortlisted bids
      queryClient.invalidateQueries({ queryKey: ['bidOptions'] });
      queryClient.invalidateQueries({ queryKey: ['shortlistedBids'] });
    },
    onError: (err) => {
      console.error('Error updating bid option:', err);
    }
  });

  const shortListBidOption = useCallback(async (bidOptionData) => {
    await shortlistMutation.mutateAsync(bidOptionData);
  }, [shortlistMutation]);

  /**
   * Calculate stats from quotes
   */
  const stats = {
    totalQuotes: quotes.length,
    accepted: quotes.filter(q => q.status === 'ACCEPTED').length,
    sent: quotes.filter(q => q.status === 'SENT').length,
    draft: quotes.filter(q => q.status === 'DRAFT').length,
  };

  return (
    <div>
      <div className="max-w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 ">

          {/* Left Column - Bids Inbox 
          <div className="flex flex-col min-h-[calc(100vh-240px)]">
            <MessagesInbox messages={allBidOptions} loading={bidsLoading} hanldeShortList={shortListBidOption} />
          </div>*/}

          {/* Right Column - Stats + Recent Activity */}
          <div className="flex flex-col gap-6 min-h-[calc(100vh-240px)] bg-white/50 backdrop-blur-sm rounded-[2rem] p-4 md:p-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-4">

              <Card className="overflow-hidden bg-white !rounded-2xl shadow-sm border !border-gray-100">
                <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-5xl font-normal text-gray-900">
                          {quotesLoading ? (
                              <div className="space-y-3">
                                <Skeleton className="h-4 w-16" />
                              </div>
                            ) : stats.totalQuotes}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Total Bids</p>
                      </div>
                      <div className="p-2 bg-blue-100 rounded-full">
                        <RiInboxLine className="w-5 h-5 text-blue-600" />
                      </div>
                    </div>
                </CardContent>
              </Card>

              <Card className="overflow-hidden bg-white !rounded-2xl shadow-sm border !border-gray-100">
                <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-5xl font-normal text-gray-900">
                          {quotesLoading ? (
                            <div className="space-y-3">
                              <Skeleton className="h-4 w-16" />
                            </div>
                          ) : stats.accepted}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Accepted</p>
                      </div>
                      <div className="p-2 bg-green-100 rounded-full">
                        <RiFileTextLine className="w-5 h-5 text-green-600" />
                      </div>
                    </div>
                </CardContent>
              </Card>

            </div>

            {/* Pending Approvals — admin/super_admin only. Junior Broker
                submissions (quote + trip sheet) awaiting admin review.
                Kept above Recent Activity because it's actionable. */}
            {canApproveSubmissions && (
              <div id="pending-approvals" className="bg-white !rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col">
                <div className="flex items-center justify-between mb-4 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <RiHourglass2Line className="w-5 h-5 text-amber-500" />
                    <h2 className="text-lg font-semibold text-gray-900">Pending Approvals</h2>
                    {pendingApprovals.length > 0 && (
                      <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                        {pendingApprovals.length}
                      </span>
                    )}
                  </div>
                </div>
                <div className="space-y-2 overflow-y-auto max-h-[300px] pr-1 [&::-webkit-scrollbar]:w-[4px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full">
                  {pendingLoading ? (
                    Array.from({ length: 2 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3 p-3">
                        <Skeleton className="w-12 h-5 rounded-md flex-shrink-0" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-4 w-40" />
                          <Skeleton className="h-3 w-56" />
                        </div>
                        <Skeleton className="h-5 w-16 rounded-full flex-shrink-0" />
                      </div>
                    ))
                  ) : pendingApprovals.length === 0 ? (
                    <div className="text-center py-6 text-sm text-gray-500">
                      No pending approvals.
                    </div>
                  ) : (
                    pendingApprovals.map((row) => {
                      const clientName = [row.client?.first_name, row.client?.last_name]
                        .filter(Boolean).join(' ') || 'Unknown Client';
                      const route = row.legs.length > 0
                        ? row.legs.map(l => `${l.departure_airport} → ${l.arrival_airport}`).join(', ')
                        : '';
                      const tagStyles = row.kind === 'TSHEET'
                        ? 'bg-blue-50 text-blue-700 border-blue-100'
                        : 'bg-amber-50 text-amber-700 border-amber-100';
                      const tagLabel = row.kind === 'TSHEET' ? 'TRIP SHEET' : 'QUOTE';
                      return (
                        <button
                          key={`${row.kind}-${row.quoteId}`}
                          type="button"
                          onClick={() => openPendingApproval(row)}
                          className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors text-left"
                        >
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-semibold tracking-wide ${tagStyles}`}>
                            {tagLabel}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {row.quoteNumber ? `${row.quoteNumber} · ` : ''}{clientName}
                            </div>
                            {route && (
                              <div className="text-xs text-gray-500 truncate flex items-center gap-1 mt-0.5">
                                <RiMapPinLine className="w-3.5 h-3.5" />{route}
                              </div>
                            )}
                          </div>
                          <RiArrowRightLine className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* Recent Activity */}
            <div className="bg-white !rounded-2xl shadow-sm border border-gray-100 p-6 flex-1 min-h-[200px] max-h-[calc(100vh-22rem)] flex flex-col">
              <div className="flex items-center justify-between mb-6 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <RiPulseFill className="w-5 h-5 text-amber-500" />
                  <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
                </div>
                {/* <button className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-2xl hover:bg-gray-50 transition-colors">
                  See All
                </button> */}
              </div>

              <div className="space-y-4 overflow-y-auto flex-1 pr-1 [&::-webkit-scrollbar]:w-[4px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full">
                {recentLoading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Skeleton className="w-8 h-8 rounded-2xl flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3.5 w-48" />
                        <div className="flex gap-2">
                          <Skeleton className="h-6 w-24 rounded-md" />
                          <Skeleton className="h-6 w-20 rounded-md" />
                        </div>
                      </div>
                      <Skeleton className="h-3 w-6 flex-shrink-0" />
                    </div>
                  ))
                ) : recentActivities.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 text-sm">No recent activities</p>
                  </div>
                ) : (
                  recentActivities.map((activity, index) => (
                    <div key={activity.id} className="flex items-start gap-3 relative">
                      {/* Vertical line connecting icons */}
                      {index < recentActivities.length - 1 && (
                        <div className="absolute left-[18px] top-[35px] bottom-[-8px] w-[2px] bg-gray-100" />
                      )}
                      <div className={`p-2 rounded-2xl ${activity.iconBg} flex-shrink-0 relative z-10`}>
                        {activity.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 text-sm">{activity.title}</h3>
                        <p className="text-sm text-gray-500 mt-0.5">{activity.description}</p>
                        {activity.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {activity.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md"
                              >
                                <span className="text-xs">{tag.icon}</span>
                                {tag.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">{activity.time}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
